import React, { useEffect, useMemo, useState } from "react";
import { Card, Input, Switch, Table, Tag, Typography, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../util/notifications";
import {
  FeatureFlagDto,
  getFeatures,
  updateFeature,
} from "./sagas";
import { useFeaturePermissionContext } from "../../context/FeaturePermissionContext";
import Unauthorized from "../unauthorized/unauthorized";

const { Title, Text } = Typography;

const FeatureManagement: React.FC = () => {
  const { isAdmin, loaded, refresh } = useFeaturePermissionContext();
  const [features, setFeatures] = useState<FeatureFlagDto[]>([]);
  const [permission, setPermission] = useState<string>("Read");
  const [userEmail, setUserEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [updating, setUpdating] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");

  const canWrite = permission === "Write";

  const filteredFeatures = useMemo(
    () =>
      features.filter((feature) =>
        feature.FeatureName.toLowerCase().includes(searchText.toLowerCase())
      ),
    [features, searchText]
  );

  const loadFeatures = async () => {
    setLoading(true);
    try {
      const response = await getFeatures();
      setFeatures(response.Features || []);
      setPermission(response.Permission || "Read");
      setUserEmail(response.UserEmail || "");
    } catch (error: any) {
      showErrorNotification("Unable to load features", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadFeatures();
    }
  }, [isAdmin]);

  const handleToggle = async (featureName: string, isEnabled: boolean) => {
    setUpdating(featureName);
    try {
      const response = await updateFeature(featureName, isEnabled);
      setFeatures(response.Features || []);
      setPermission(response.Permission || "Read");
      refresh();
      showSuccessNotification(
        `${featureName} ${isEnabled ? "enabled" : "disabled"} successfully`
      );
    } catch (error: any) {
      showErrorNotification("Unable to update feature", error);
    } finally {
      setUpdating("");
    }
  };

  const columns = [
    {
      title: "Feature",
      dataIndex: "FeatureName",
      key: "FeatureName",
    },
    {
      title: "Type",
      dataIndex: "IsBackground",
      key: "IsBackground",
      render: (isBackground: boolean) =>
        isBackground ? <Tag color="purple">Timer / Queue</Tag> : <Tag>Screen</Tag>,
    },
    {
      title: "Status",
      dataIndex: "IsEnabled",
      key: "IsEnabled",
      render: (isEnabled: boolean) =>
        isEnabled ? (
          <Tag color="green">Enabled</Tag>
        ) : (
          <Tag color="red">Disabled</Tag>
        ),
    },
    {
      title: canWrite ? "Enable / Disable" : "Access",
      key: "action",
      render: (_: any, record: FeatureFlagDto) =>
        canWrite ? (
          <Switch
            checked={record.IsEnabled}
            loading={updating === record.FeatureName}
            onChange={(checked) => handleToggle(record.FeatureName, checked)}
          />
        ) : (
          <Tag>Read only</Tag>
        ),
    },
  ];

  if (!loaded) {
    return <Spin style={{ display: "block", margin: 40 }} />;
  }

  if (!isAdmin) {
    return <Unauthorized />;
  }

  return (
    <Card style={{ margin: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Feature Management
        </Title>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search features"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </div>
      <Text type="secondary">
        {canWrite
          ? "You have write permission. You can enable or disable features."
          : "You have read-only permission on enabled features."}
      </Text>
      <Spin spinning={loading}>
        <Table
          style={{ marginTop: 16 }}
          rowKey="FeatureName"
          columns={columns}
          dataSource={filteredFeatures}
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Spin>
    </Card>
  );
};

export default FeatureManagement;
